import React, {useCallback, useState, useRef, useEffect} from 'react';

import {MAX_GRID_TITLE_NAME_LENGTH} from 'notebook/canvas/CanvasConstants';
import EditableTextField from 'ui-library/inputs/EditableTextField';

interface GridTitleNameEditorProps {
  name: string;
  onRename: (newValue: string | number) => Promise<void>;
  titleDisabled?: boolean;
}

export default function GridTitleNameEditor(props: GridTitleNameEditorProps): JSX.Element {
  const {onRename: onRenameHandler} = props;
  const [name, setName] = useState(props.name);
  const [isDisabled, setIsDisabled] = useState(props.titleDisabled ?? false);
  const displayName = isDisabled ? name : props.name;
  const mountedRef = useRef(true);

  /**
   * While the onRename callback is running (and probably invoking the API), we use a placeholder value.
   * Otherwise, the name that comes on props will be shown briefly.
   */
  const onRename = useCallback(
    async (newValue: string | number): Promise<void> => {
      setIsDisabled(true);
      setName(String(newValue));
      await onRenameHandler(newValue);

      // do not try to set state if the component is unmounted
      // this can happen if the async onRenameHandler caused this component to
      // unmount (for example by writing new data to the apollo cache)
      if (!mountedRef.current) {
        return;
      }

      setIsDisabled(false);
    },
    [onRenameHandler]
  );

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  return (
    <EditableTextField
      requestChange={onRename}
      value={displayName}
      maxLength={MAX_GRID_TITLE_NAME_LENGTH}
      isDisabled={isDisabled}
      dataTestAttrs={{component: 'GridTitleNameEditor', element: 'title'}}
    />
  );
}
